import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserMailAccountContract } from '@app-types/api/user-mail-account';
import { UserMailAccountClient } from '@app-services/api/clients/user-mail-account.client';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
  BaseGetSearchCountByIdRequest,
} from '@app-types/base/base';
import { PermissionService } from '@app-services/permission/permission.service';
import { RoleType } from '@app-types/enums/role-type';
import { MatDialog } from '@angular/material/dialog';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { OrganizationSettingsClient } from 'src/app/services/api/clients/organization-settings.client';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SearchBarComponent } from '@app-components/common/search-bar/search-bar.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import {
  MAIL_ACCOUNT_ID,
  SETTING_SETTINGS,
  defaultFilter,
} from '@app-shared/constants/constants';
import { DrawerService } from '@app-services/drawer.service';
import { MailAccountUsersAddComponent } from '@app-components/settings/organization/mail-account/mail-account-users-add/mail-account-users-add.component';
import { DeleteDialogComponent } from '@app-components/common/delete-dialog/delete-dialog.component';
import { Subscription } from 'rxjs';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { InfinityScrollComponent } from '@app-components/common/infinity-scroll/infinity-scroll.component';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FilterFields, OptionsEnum } from '@app-types/common';
import { UserMailAccountSearchOrder } from '@app-types/enums/UserMailAccountSearchOrder';
import { FormatUsersMailAccountSearchOrders } from '@app-pipes/format-users-mail-account-search-orders.pipe';
import { OrderDirection } from '@app-types/enums/order-direction';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';

const { PAGE_SIZE, STEP_FETCH } = SETTING_SETTINGS;

@Component({
  selector: 'app-mail-account-users',
  templateUrl: './mail-account-users.component.html',
  styleUrls: ['./mail-account-users.component.scss'],
  imports: [
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    SearchBarComponent,
    NoDataComponent,
    SomethingWentWrongComponent,
    MatTableModule,
    MatTooltipModule,
    MatCardModule,
    MailAccountUsersAddComponent,
    SkeletonTableComponent,
    TableHeaderComponent,
    InfinityScrollComponent,
    MatSort,
    MatSortModule,
    LoaderComponent,
  ],
})
export class MailAccountUsersComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'setting-container';
  public organizationId: number | undefined;
  public mailAccountId: number | null;
  public isLoading = true;
  public iscredemISAMSupport = true;
  public isError = false;
  public dataSource = new MatTableDataSource<UserMailAccountContract>([]);
  public displayedColumns: string[] = [
    'firstName',
    'lastName',
    'GAW',
    'adfs',
    'email',
    'role',
    'actions',
  ];
  public roleTypes = RoleType;
  public searchValue = '';
  public canNavigateForward = false;
  public isLoadingScroll = false;
  public isErrorScroll = false;
  public offset = 0;
  public stepFetch = STEP_FETCH;
  public pageSize = PAGE_SIZE;
  public totalSize = 0;
  public editingUserMailAccount: UserMailAccountContract | null = null;
  public useMobileView = false;
  private useMobileViewSubscription: Subscription;
  public searchOrder: UserMailAccountSearchOrder | 0 = 0;
  public searchOrders: OptionsEnum[];
  public filter: FilterFields<UserMailAccountSearchOrder> = {
    ...defaultFilter,
  };
  public isExporting = false;
  private formatUsersMailAccountSearchOrders =
    new FormatUsersMailAccountSearchOrders();
  isReloadLoading = false;
  private suppressSortDrivenSearch = false;
  @ViewChild('addDrawer') addDrawer: TemplateRef<any>;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public matchError: MatchError,
    protected router: Router,
    protected route: ActivatedRoute,
    private userMailAccountClient: UserMailAccountClient,
    private permissionService: PermissionService,
    public dialog: MatDialog,
    public organizationSettingsClient: OrganizationSettingsClient,
    private mobileObserverService: MobileObserverService,
    private drawerService: DrawerService,
    private userWorkspaceService: UserWorkspaceService,
    private tempFileClient: TempFileClient
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    this.organizationId =
      this.userWorkspaceService.currentOrganization?.organizationId;

    this.searchOrders = this.displayedColumns
      .filter(el => el !== 'actions')
      .map(key => ({
        id: this.formatUsersMailAccountSearchOrders.transform(key),
        name: key,
      }));

    const mailAccId = this.route.snapshot.paramMap.get(MAIL_ACCOUNT_ID);
    this.mailAccountId = mailAccId ? +mailAccId : null;
    await this.loadMailAccountUsers(true);
  }

  async loadMailAccountUsers(fetchCounter: boolean): Promise<void> {
    if (!this.organizationId) return;
    this.canNavigateForward = false;
    const loaderTimeout =
      !this.offset && !this.dataSource.data.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;

    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const request = {
        mailAccountId: this.mailAccountId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter: this.filter,
        search: this.searchValue ? this.searchValue : null,
      };
      const organizationSettings =
        await this.organizationSettingsClient.getByOrganization(
          new BaseGetByIdRequest(this.organizationId)
        );
      this.iscredemISAMSupport = organizationSettings.result.credemISAMSupport;
      const response =
        await this.userMailAccountClient.searchMailAccountUsers(request);

      const userMailAccounts = response.data.filter(
        e =>
          e?.userEmail &&
          this.permissionService.hasPermissionToSeeUserByUserEmail(e.userEmail)
      );
      this.dataSource.data =
        this.offset === 0
          ? userMailAccounts
          : [...this.dataSource.data, ...userMailAccounts];

      if (fetchCounter) {
        const countRequest = new BaseGetSearchCountByIdRequest(
          this.mailAccountId,
          this.searchValue ? this.searchValue : null
        );
        const counterResponse =
          await this.userMailAccountClient.getSearchMailAccountUsersCount(
            countRequest
          );

        this.totalSize = counterResponse.result;
      }
      const countHidden = response.data.length - userMailAccounts.length;
      this.totalSize = this.totalSize - countHidden;
      this.canNavigateForward = this.totalSize !== this.dataSource.data.length;
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
      } else {
        this.isError = true;
      }
      this.matchError.logError(e);
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  public onCreateMailAccountUserBtnClicked(): void {
    this.drawerService.openDrawer(this.addDrawer);
  }

  async onAddUserToMailAccount(): Promise<void> {
    await this.onSearch();
  }

  public onEditMailAccountUserBtnClicked(
    userMailAccount: UserMailAccountContract
  ): void {
    this.editingUserMailAccount = userMailAccount;
    this.drawerService.openDrawer(this.addDrawer);
  }

  onCloseEditModalForm(): void {
    this.editingUserMailAccount = null;
  }

  async onDeleteBtnClicked(userMailAccountId: number): Promise<void> {
    await this.userMailAccountClient.deleteMailAccountUser({
      id: userMailAccountId,
    });
  }

  public deleteUserMailAccount(
    userMailAccountId: number,
    mailAccountName: string,
    userFirstName: string,
    userLastName: string
  ): void {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '450px',
      autoFocus: false,
      data: {
        title: 'confirmUserDeletion',
        subTitles: [
          {
            title: 'deleteUser',
            subTitle: userFirstName + ' ' + userLastName,
          },
          {
            title: 'fromCompany',
            subTitle: mailAccountName,
          },
        ],
        onDelete: async () => await this.onDeleteBtnClicked(userMailAccountId),
      },
    });

    dialogRef.afterClosed().subscribe(async x => {
      if (x?.isDeleted) {
        this.onSearch();
      }
    });
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.pageSize;
    await this.loadMailAccountUsers(false);
  }

  async refreshData(): Promise<void> {
    await this.loadMailAccountUsers(false);
  }

  resetScrollData(): void {
    this.offset = 0;
  }

  async reload(): Promise<void> {
    this.isReloadLoading = true;
    this.filter = { ...defaultFilter };
    this.searchOrder = 0;
    this.suppressSortDrivenSearch = true;
    this.clearSort();
    try {
      await this.onSearch();
    } finally {
      this.isReloadLoading = false;
      setTimeout(() => {
        this.suppressSortDrivenSearch = false;
      }, 0);
    }
  }

  async onSearch(fetchSearchCounter = true): Promise<void> {
    this.resetScrollData();
    this.infinityScroll?.scrollTop();
    await this.loadMailAccountUsers(fetchSearchCounter);
  }

  clearSort(): void {
    if (!this.sort) {
      return;
    }
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
    this.sort.active = '';
    this.sort.direction = '';
  }

  async changeOrder(): Promise<void> {
    this.filter.orderDirection =
      this.filter.orderDirection === OrderDirection.Ascending
        ? OrderDirection.Descending
        : OrderDirection.Ascending;
    await this.onSearch();
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.searchOrder = $event as UserMailAccountSearchOrder;
    this.filter.searchOrder = this.searchOrder;
    this.filter.orderDirection = OrderDirection.Ascending;
    await this.onSearch();
  }

  async sortData(sort: Sort): Promise<void> {
    if (this.suppressSortDrivenSearch) {
      return;
    }
    this.filter.searchOrder = sort.direction
      ? this.formatUsersMailAccountSearchOrders.transform(sort.active)
      : UserMailAccountSearchOrder.OrderByUserMailAccountId;

    this.filter.orderDirection =
      sort.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.onSearch();
  }

  async exportExcel(): Promise<void> {
    this.isExporting = true;
    try {
      const fileResponse = await this.userMailAccountClient.exportExcel(
        new BaseGetByIdRequest(this.mailAccountId)
      );
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  async retryData(): Promise<void> {
    await this.loadMailAccountUsers(true);
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.useMobileViewSubscription?.unsubscribe();
  }
}
